<template>
  <li>
    <div class="posts-com-feed">
      <div class="page-caption-posts">
        <img v-if="page.owner_avatar" :src="page.owner_avatar" alt />
        <div>
          <p>{{ post.page_name }}</p>
          <span>
            {{ socialLabel }}
            <template v-if="post.screen_name">· @{{ post.screen_name }}</template>
          </span>
        </div>
      </div>
    </div>

    <div class="post-body">
      <div class="image" v-if="collageItems.length && !showPostEmbed">
        <collage
          ref="collage"
          :items="collageItems"
          :className="'paper-collage'"
          :padding="5"
          :tuid="randomHash"
          :badges="visionBadges"
          @badge-click="onBadge"
          @video-error="onCollageMediaError"
          @photo-error="onCollageMediaError"
        />
      </div>

      <div class="image" v-if="showPostEmbed">
        <Cm-ig-embed :url="post.link || post.reelEmbed" />
      </div>

      <div class="cm-embeds image" v-if="post.embeds && post.embeds.length">
        <div class="cm-embed" v-for="emb in post.embeds" :key="'emb-' + emb.index">
          <span
            v-if="imageAnalysis"
            class="collage-badge"
            :class="analyzedSet.has(emb.index) ? 'is-done' : 'is-new'"
            @click="askAnalyse({ index: emb.index, url: emb.url, video: true })"
          >
            <i :class="analyzedSet.has(emb.index) ? 'bi bi-check-lg' : 'bi bi-question-lg'"></i>
          </span>
          <iframe
            :src="emb.url"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture;"
            frameborder="0"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
      </div>

      <div class="text">
        <a class="date" :href="post.link" target="_blank" v-if="post.link">
          {{ getTime }}
          <mod>{{ getDate }}</mod>
        </a>

        <p class="post-text">
          <span v-if="post.text" v-html="displayText"></span>
          <span
            class="more"
            v-if="canShowMore"
            @click="expanded = !expanded"
          >{{ expanded ? $t('POST_LESS') : $t('POST_MORE') }}</span>
        </p>

        <div class="cm-chips" v-if="hasChips">
          <!-- чип несёт свой источник: слово текста открывает drill-down по тексту,
               с какой бы вкладки ни кликнули (иначе с вкладки «Картинки» текстовое
               слово искалось бы среди тегов картинок и сам пост в стате не находился) -->
          <span
            v-for="t in post.terms"
            :key="'t' + t"
            class="cm-chip"
            @click="openTerm(t, 'text')"
          >{{ t }}
          <span class="cm-term"><i class="bi bi-box-arrow-up-right"></i></span>
          </span>
          <!-- бренды и известные люди из текста поста (разметка моделью) -->
          <span
            v-for="b in textBrandChips"
            :key="'tb' + b.key"
            class="cm-chip"
            :title="$t('CM_CHIP_BRAND_TEXT')"
            @click="openTerm(b.key, 'text')"
          >{{ b.label }}
          <span class="cm-term"><i class="bi bi-box-arrow-up-right"></i></span>
          </span>
          <!-- бренды и известные люди с картинок — всегда, их сервер не режет;
               оформление у всех чипов одно (без значков — так просил пользователь) -->
          <span
            v-for="b in imageBrandChips"
            :key="'b' + b.key"
            class="cm-chip"
            :title="$t('CM_CHIP_BRAND')"
            @click="openTerm(b.key, 'media')"
          >{{ b.label }}
          <span class="cm-term"><i class="bi bi-box-arrow-up-right"></i></span>
          </span>
          <!-- что на картинках: объекты, действия, место, атрибуты -->
          <span
            v-for="m in post.mediaTerms"
            :key="'m' + m.key"
            class="cm-chip"
            :title="$t('CM_CHIP_IMAGE')"
            @click="openTerm(m.key, 'media')"
          >{{ m.label }}
          <span class="cm-term"><i class="bi bi-box-arrow-up-right"></i></span>
          </span>
        </div>
      </div>
    </div>

    <div class="m-tech">
      <div class="reactions">
        <div class="react-metric" v-if="post.views">
          <span><i class="bi bi-eye"></i></span>
          {{ formatNum(post.views) }}
        </div>
        <div class="react-metric" v-if="post.comments != null">
          <span><i class="bi bi-chat-right-dots"></i></span>
          {{ formatNum(post.comments) }}
        </div>
        <div class="react-metric" v-if="post.reposts != null">
          <span><i class="bi bi-arrow-return-left"></i></span>
          {{ formatNum(post.reposts) }}
        </div>
        <div class="react-metric" v-if="post.likes != null">
          <span><i class="bi bi-heart"></i></span>
          {{ formatNum(post.likes) }}
        </div>
      </div>

      <div class="engagement">
        <div class="react-metric" v-if="post.er != null">
          <span>ER</span>
          {{ post.er }}%
        </div>
        <div class="react-metric" v-if="post.relative_er != null">
          <span>{{ $t('CM_COL_REL_ER') }}</span>
          <span :class="relClass(post.relative_er)">{{ post.relative_er }}%</span>
        </div>
        <div class="react-metric" v-if="post.tr != null">
          <span>TR</span>
          {{ post.tr }}%
        </div>
        <div class="react-metric" v-if="post.relative_tr != null">
          <span>{{ $t('CM_COL_REL_TR') }}</span>
          <span :class="relClass(post.relative_tr)">{{ post.relative_tr }}%</span>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import Collage from "../PostsFeed/Collage";
import CmIgEmbed from "./Cm-ig-embed";
import pageUtils from "@/models/page-utils";
import bus from "@/models/content-monitoring/bus";

const SOCIALS = { FB: "Facebook", IG: "Instagram", VK: "VK", TG: "Telegram" };
const TEXT_LIMIT = 300;

export default {
  name: "Cm-feed-one",
  components: { Collage, CmIgEmbed },
  props: {
    post: {
      type: Object,
      required: true,
    },
    page: {
      type: Object,
      default: () => ({}),
    },
    layout: {
      type: String,
      default: "list",
    },
    // разбор картинок доступен только на платном тарифе (признак приходит с лентой)
    imageAnalysis: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      expanded: false,
      brokenMedia: {},
    };
  },
  computed: {
    ...mapGetters(["getCmFormat"]),

    /** Один бренд бывает и в тексте, и на картинке того же поста — чип показываем
     * один, со стороны текущей вкладки (на «всё» — текстовый). */
    textBrandChips() {
      const list = this.post.textBrands || [];
      if (this.getCmFormat !== "media") return list;
      const seen = new Set((this.post.brands || []).map((b) => b.key));
      return list.filter((b) => !seen.has(b.key));
    },
    imageBrandChips() {
      const list = this.post.brands || [];
      if (this.getCmFormat === "media") return list;
      const seen = new Set((this.post.textBrands || []).map((b) => b.key));
      return list.filter((b) => !seen.has(b.key));
    },

    hasChips() {
      const p = this.post;
      return !!(
        (p.terms && p.terms.length) ||
        (p.textBrands && p.textBrands.length) ||
        (p.brands && p.brands.length) ||
        (p.mediaTerms && p.mediaTerms.length)
      );
    },
    socialLabel() {
      return SOCIALS[this.post.social] || this.post.social || "";
    },
    collageItems() {
      const embed = this.post.reelEmbed || "";
      const link = this.post.link || "";
      return (this.post.images || []).map((item) => ({
        ...item,
        embed_url: embed,
        post_url: link,
      }));
    },
    isLoneVideo() {
      return this.collageItems.length === 1 && this.collageItems[0].type === "video";
    },
    allMediaBroken() {
      const n = this.collageItems.length;
      if (!n) return false;
      for (let i = 0; i < n; i += 1) {
        if (!this.brokenMedia[i]) return false;
      }
      return true;
    },
    showPostEmbed() {
      if (!this.post.reelEmbed) return false;
      if (!this.collageItems.length) return true;
      if (this.isLoneVideo && this.brokenMedia[0]) return true;
      return this.allMediaBroken;
    },
    canShowMore() {
      return this.normalizedText.length > TEXT_LIMIT;
    },
    normalizedText() {
      return pageUtils.replaceTextBreaks(this.post.text || "");
    },
    displayText() {
      let text = this.normalizedText;
      if (!text) return "";
      if (!this.expanded && this.canShowMore) {
        text = `${text.slice(0, TEXT_LIMIT).trimEnd()}…`;
      }
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    },
    getTime() {
      const d = moment(this.post.date);
      return d.isValid() ? d.format("HH:mm") : "";
    },
    getDate() {
      const d = moment(this.post.date);
      return d.isValid() ? d.format("DD/MM/YYYY") : "";
    },
    randomHash() {
      return Math.random().toString(36).substr(2, 8);
    },
    /** Разобранные вложения: пришедшие с сервера плюс разобранные только что. */
    analyzedSet() {
      const ids = this.post.analyzed || [];
      return new Set(ids.map(Number));
    },
    /** Значок на каждую плитку коллажа: true — картинка уже разобрана. */
    visionBadges() {
      if (!this.imageAnalysis) return [];   // тестовый доступ — значка «разобрать» нет
      return this.collageItems.map((item) => this.analyzedSet.has(Number(item.index)));
    },
  },
  watch: {
    "post.id"() {
      this.brokenMedia = {};
    },
    layout() {
      this.relayoutCollage();
    },
  },
  methods: {
    relayoutCollage() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const collage = this.$refs.collage;
            if (collage && typeof collage.createCollage === "function") {
              collage.createCollage();
            }
          });
        });
      });
    },
    onCollageMediaError(_item, idx) {
      if (idx == null || idx < 0) return;
      this.$set(this.brokenMedia, idx, true);
    },
    formatNum(num) {
      return pageUtils.formatNumbers(num);
    },
    relClass(value) {
      return ["cm-rel", value >= 100 ? "is-up" : "is-down"];
    },
    /**
     * Клик по чипу открывает drill-down слова. Чип с картинки несёт source:
     * его статистику считают по картинкам, на какой бы вкладке ни была лента
     * (в тексте такого слова может не быть вовсе).
     */
    openTerm(term, source) {
      bus.$emit("cm:term", term, source);
    },

    /**
     * Клик по кнопке открывает запрос к модели: его правят перед разбором,
     * чтобы подобрать состав описания (текст запоминается в браузере).
     */
    /** Клик по значку плитки коллажа. */
    onBadge(idx) {
      const item = this.collageItems[idx];
      if (!item) return;
      this.askAnalyse({ index: item.index, url: item.image_url || item.video_url,
                        video: item.type === "video" });
    },

    /**
     * Разбор картинки ведёт ЛЕНТА, а не карточка: модалка должна жить вне
     * плитки, которую masonry двигает трансформом (внутри неё ломается
     * position: fixed, а перенос модалки в body ломал патч Vue — соседняя
     * модалка не вставлялась и результат не показывался).
     */
    askAnalyse(target) {
      if (!this.imageAnalysis || !target) return;
      this.$emit("analyse", {
        post: this.post,
        target,
        analyzed: this.analyzedSet.has(Number(target.index)),
      });
    },
  },
};
</script>

<style scoped>
.cm-embeds {
  margin: 8px 0;
}
.cm-embed {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}
.cm-embed:last-child {
  margin-bottom: 0;
}
.cm-embed iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
